/**
 * Voz del Despacho (ola 1): nota de voz → texto y respuesta → audio.
 * Misma sesión (Bearer) que el resto de `/despacho/*`.
 *
 * - `transcribe`: el cuerpo es el AUDIO CRUDO, con su `Content-Type`
 *   (`audio/webm`, `audio/ogg`, `audio/mp4`, `audio/wav`, `audio/mpeg`;
 *   se admite `;codecs=…`), hasta `VOICE_LIMITS.audioBytes`.
 * - `speak`: JSON `{ text }` (el `reply` tal cual; la API lo limpia para
 *   voz) → `audio/mpeg`.
 */
import { z } from "zod";

export const VOICE_ROUTES = {
  transcribe: "/despacho/voice/transcribe",
  speak: "/despacho/voice/speak",
} as const;

export const VOICE_LIMITS = {
  /** 10 MB: holgado para 3 min de opus (~1 MB) o de wav comprimido. */
  audioBytes: 10 * 1024 * 1024,
  /** Caracteres del `reply` que se aceptan para sintetizar. */
  speakChars: 6000,
} as const;

/** Tipos de audio que acepta `transcribe` (sin parámetros). */
export const VOICE_AUDIO_TYPES = ["audio/webm", "audio/ogg", "audio/mp4", "audio/x-m4a", "audio/wav", "audio/x-wav", "audio/wave", "audio/mpeg"] as const;

/** Respuesta de `transcribe`. `text` vacío = no se entendió nada (silencio). */
export const TranscribeOutcome = z.object({ text: z.string() });
export type TranscribeOutcome = z.infer<typeof TranscribeOutcome>;

export const SpeakRequest = z.object({ text: z.string().trim().min(1).max(VOICE_LIMITS.speakChars) });
export type SpeakRequest = z.infer<typeof SpeakRequest>;

/**
 * Códigos de error de la voz (cuerpo `{ code, message }`, como el resto
 * del Despacho): `session` 401 · `audio` 400 (vacío, tipo no admitido o
 * ilegible) · `too-large` 413 · `invalid` 400 (texto vacío o largo) ·
 * `voice` 502 (el proveedor falló).
 */
export type VoiceErrorCode = "session" | "audio" | "too-large" | "invalid" | "voice";
